import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { Loader2, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  DndContext,
  DragOverlay,
  MeasuringStrategy,
  PointerSensor,
  KeyboardSensor,
  closestCorners,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import PageHeader from "../PageHeader";
import TaskColumn from "./TaskColumn";
import DoneColumnFooter from "./DoneColumnFooter";
import { TaskCardView } from "./TaskCard";
import ProjectPicker from "./ProjectPicker";
import LiveStatus from "./LiveStatus";
import TaskModal from "./TaskModal";
import useTasks from "../../hooks/useTasks";
import usePoll from "../../hooks/usePoll";
import useProjects from "../../hooks/useProjects";
import useHouseholdUsers from "../../hooks/useHouseholdUsers";
import AuthContext from "../../context/AuthContext";
import { taskApi } from "../../services/api";
import { TASK_STATUS } from "../../constants";
import {
  byBoardOrder,
  computeDragPatches,
  resolveDropTarget,
  COLUMN_DROP_PREFIX,
} from "./dragReorder";

// Column order is fixed by the spec; do not reorder without updating the spec.
const COLUMNS = [
  { status: TASK_STATUS.TODO, label: "TODO" },
  { status: TASK_STATUS.IN_PROGRESS, label: "In Progress" },
  { status: TASK_STATUS.BLOCKED, label: "Blocked" },
  { status: TASK_STATUS.DONE, label: "Done" },
];

// How often an open board asks whether anything changed.
const LIVE_POLL_MS = 5000;

const scopeFromSearchParams = (searchParams) => {
  const raw = searchParams.get("project");
  if (!raw) return "all";
  return raw;
};

function TaskBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = scopeFromSearchParams(searchParams);

  const { tasks, loading, error, refresh, createTask, updateTask, deleteTask } =
    useTasks(scope);
  const { projects, loading: projectsLoading } = useProjects();
  const {
    users,
    error: usersError,
    refresh: refreshUsers,
  } = useHouseholdUsers();
  // The signed-in user's id, from the JWT claims (simplejwt's `user_id`).
  const auth = useContext(AuthContext);
  const me = auth?.user?.user_id ?? null;

  // How many archived tasks this board's scope has (for the Done footer).
  // "none" (tasks without a project) has no API filter, so no count there.
  const [archivedCount, setArchivedCount] = useState(null);
  // Only the newest count request may land: switching projects quickly
  // must not let the previous scope's count overwrite this one's.
  const archivedCountRequest = useRef(0);
  const refreshArchivedCount = useCallback(async () => {
    const request = ++archivedCountRequest.current;
    const projectId = Number(scope);
    if (scope !== "all" && !Number.isFinite(projectId)) {
      setArchivedCount(null);
      return;
    }
    try {
      const params = { archived: "true" };
      if (Number.isFinite(projectId)) params.project = projectId;
      const { data } = await taskApi.getAll(params);
      if (request === archivedCountRequest.current) {
        setArchivedCount(data?.count ?? null);
      }
    } catch {
      // the footer falls back to "View archive"
      if (request === archivedCountRequest.current) setArchivedCount(null);
    }
  }, [scope]);
  useEffect(() => {
    refreshArchivedCount();
  }, [refreshArchivedCount]);

  // Live (projects/task-live-refresh): ask the cheap pulse every few
  // seconds while this tab is visible; refetch the board only when its
  // fingerprint moves. The first pulse of a scope is its baseline — it
  // fires at once, alongside the board's own first fetch, so a change
  // landing between the two still moves the next pulse. A refetch that
  // lands mid-drag is deferred by pendingSyncRef below, like any other.
  //
  // Every await is followed by a stale() check: a tick belongs to the
  // scope it started in, and a switch mid-tick must not let it refresh
  // with the old scope's fetch (review round, PR #327). The new stamp is
  // committed only once the refetch has landed, so a failed refetch is
  // retried on the next tick (and shows "Reconnecting…") rather than
  // forgotten; and while a drag's PATCHes are saving, the pulse waits —
  // the drag reconciles itself when they land.
  // A drop's PATCHes in flight: a background refetch that started before
  // the drop and lands before they do carries the PRE-drop board, and
  // would snap the card back until the drag's own reconcile arrives
  // (review round, PR #327). Held like a drag; the reconcile supersedes it.
  const patchingRef = useRef(0);
  const pulseRef = useRef({ scope: null, stamp: null });
  const { status: liveStatus } = usePoll(
    async ({ stale }) => {
      const projectId = Number(scope);
      const { data } = await taskApi.pulse(
        Number.isFinite(projectId) ? { project: projectId } : undefined,
      );
      if (stale()) return;
      const seen = pulseRef.current;
      if (seen.scope !== scope) {
        pulseRef.current = { scope, stamp: data.stamp }; // the baseline
        return;
      }
      if (seen.stamp === data.stamp || patchingRef.current > 0) return;
      const landed = await refresh({ background: true });
      if (stale()) return;
      if (landed === false) throw new Error("the board couldn't be refreshed");
      pulseRef.current = { scope, stamp: data.stamp };
      refreshArchivedCount();
    },
    { intervalMs: LIVE_POLL_MS, immediate: true, resetKey: scope },
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [modalTask, setModalTask] = useState(null);
  const [deepLinkMiss, setDeepLinkMiss] = useState(null);

  // ?task=<id> (from an agent's activity feed) opens that task's modal once
  // the board has loaded, then drops the param so a reload doesn't reopen
  // it. A task that isn't on this board (private to someone else, deleted)
  // gets a dismissible note rather than silence.
  const deepLinkId = Number(searchParams.get("task"));
  useEffect(() => {
    // Wait for a settled board: a failed fetch must not blame the task.
    if (!Number.isFinite(deepLinkId) || deepLinkId <= 0 || loading || error) {
      return;
    }
    const found = tasks.find((t) => t.id === deepLinkId);
    if (found) {
      setModalTask(found);
      setModalOpen(true);
      setDeepLinkMiss(null);
    } else {
      // Not on this board: it may be archived (the board leaves those
      // out) or in another project. Ask for it directly; only a real miss
      // (hidden or deleted) gets the note.
      const id = deepLinkId;
      taskApi
        .get(id)
        .then(({ data }) => {
          setModalTask(data);
          setModalOpen(true);
          setDeepLinkMiss(null);
        })
        .catch(() => setDeepLinkMiss(id));
    }
    const next = new URLSearchParams(searchParams);
    next.delete("task");
    setSearchParams(next, { replace: true });
  }, [deepLinkId, loading, error, tasks, searchParams, setSearchParams]);

  // Optimistic copy of tasks — mutated during drag, synced from server on
  // every refetch. Using a separate state variable lets the UI reflect the
  // new order before the PATCH round-trip completes.
  const [boardTasks, setBoardTasks] = useState([]);
  // Refetches now happen in the background, so one can land mid-drag —
  // don't let it clobber the optimistic drag state; stash it instead and
  // let the end of the drag consume it. A ref (not state) so ending a
  // drag doesn't re-run the effect with stale `tasks` and undo the
  // optimistic drop.
  const draggingRef = useRef(false);
  const pendingSyncRef = useRef(null);
  useEffect(() => {
    if (draggingRef.current || patchingRef.current > 0) {
      pendingSyncRef.current = tasks;
      return;
    }
    pendingSyncRef.current = null;
    setBoardTasks(tasks);
  }, [tasks]);

  // Keep the open task fresh: when the live board refetches, the modal's
  // row follows, so other people's edits reach an open task (the modal
  // decides what it may apply — projects/task-live-refresh D8). A task
  // that isn't on the board (opened from the archive) stays as it was.
  useEffect(() => {
    setModalTask((open) => {
      if (!open) return open;
      const fresh = tasks.find((t) => t.id === open.id);
      return fresh ?? open;
    });
  }, [tasks]);

  const [dragError, setDragError] = useState(null);

  // If the currently-selected project is no longer in the list (e.g., it was
  // just deleted from /tasks/projects), fall back to the "all" scope. Gate on
  // projectsLoading rather than projects.length so the reset still fires when
  // the user genuinely has zero projects and holds a stale ?project= URL.
  useEffect(() => {
    if (scope === "all" || scope === "none") return;
    if (projectsLoading) return;
    const numericId = Number(scope);
    if (!Number.isFinite(numericId)) return;
    const stillExists = projects.some((p) => p.id === numericId);
    if (!stillExists) {
      const next = new URLSearchParams(searchParams);
      next.delete("project");
      setSearchParams(next, { replace: true });
    }
  }, [scope, projectsLoading, projects, searchParams, setSearchParams]);

  const projectsById = useMemo(() => {
    const map = {};
    for (const project of projects) {
      map[project.id] = project;
    }
    return map;
  }, [projects]);

  // Group the (optimistic) boardTasks by status, honouring position order.
  // CANCELED tasks are never rendered on the board.
  const tasksByStatus = useMemo(() => {
    const grouped = {
      [TASK_STATUS.TODO]: [],
      [TASK_STATUS.IN_PROGRESS]: [],
      [TASK_STATUS.BLOCKED]: [],
      [TASK_STATUS.DONE]: [],
    };
    for (const task of boardTasks) {
      if (task.status === TASK_STATUS.CANCELED) continue;
      if (grouped[task.status]) {
        grouped[task.status].push(task);
      }
    }
    for (const status of Object.keys(grouped)) {
      // Same comparator the drag math uses — positions tie constantly
      // (they default to 0), and the rendered order must be the order
      // resolveDropTarget computes slots for.
      grouped[status].sort(byBoardOrder);
    }
    return grouped;
  }, [boardTasks]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      // Small activation distance so a quick click on a card still opens
      // the edit modal; once the pointer moves past 5px we begin dragging.
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
      // dnd-kit's default start keys include Enter, which is the card's
      // "open the editor" key — Space alone starts and ends a drag.
      keyboardCodes: {
        start: ["Space"],
        cancel: ["Escape"],
        end: ["Space", "Tab"],
      },
    }),
  );

  // Drag lifecycle. Three pieces of transient state:
  //   - dragSnapshotRef: the board as the SERVER believes it, captured at
  //     pick-up. handleDragOver mutates boardTasks mid-drag, so patches and
  //     rollbacks must be computed against this, not against boardTasks.
  //   - activeDragTask: feeds the DragOverlay (the floating card).
  const dragSnapshotRef = useRef(null);

  const [activeDragTask, setActiveDragTask] = useState(null);

  const handleDragStart = ({ active }) => {
    dragSnapshotRef.current = boardTasks;
    draggingRef.current = true;
    setActiveDragTask(boardTasks.find((t) => t.id === active.id) || null);
  };

  // Move the dragged task between columns WHILE dragging, so the target
  // column parts to make room in real time instead of the card teleporting
  // on release. The temporary position is fractional (±0.5 around the
  // hovered card) — purely client-side ordering; handleDragEnd computes the
  // real integer positions from the drag-start snapshot.
  const handleDragOver = ({ active, over }) => {
    if (!over) return;
    const activeTask = boardTasks.find((t) => t.id === active.id);
    if (!activeTask) return;

    let destStatus;
    let tempPosition;
    if (typeof over.id === "string" && over.id.startsWith(COLUMN_DROP_PREFIX)) {
      destStatus = over.id.slice(COLUMN_DROP_PREFIX.length);
      if (destStatus === activeTask.status) return;
      const dest = boardTasks.filter(
        (t) => t.status === destStatus && t.id !== active.id,
      );
      tempPosition = dest.length
        ? Math.max(...dest.map((t) => t.position)) + 1
        : 0;
    } else {
      const overTask = boardTasks.find((t) => t.id === over.id);
      if (!overTask || overTask.status === activeTask.status) return;
      destStatus = overTask.status;
      // Land above or below the hovered card depending on where the
      // pointer is — the same rule dnd-kit's preview uses.
      const isBelowOver =
        active.rect.current.translated != null &&
        active.rect.current.translated.top >
          over.rect.top + over.rect.height / 2;
      tempPosition = isBelowOver
        ? overTask.position + 0.5
        : overTask.position - 0.5;
    }

    setBoardTasks((prev) =>
      prev.map((t) =>
        t.id === active.id
          ? { ...t, status: destStatus, position: tempPosition }
          : t,
      ),
    );
  };

  const endDrag = () => {
    setActiveDragTask(null);
    dragSnapshotRef.current = null;
    draggingRef.current = false;
  };

  // Restore the board after a drag that changes nothing on the server:
  // server truth if a refetch was skipped mid-drag, else the snapshot
  // (which also cleans up any fractional position handleDragOver left).
  const restoreBoard = (snapshot) => {
    setBoardTasks(pendingSyncRef.current ?? snapshot);
    pendingSyncRef.current = null;
  };

  const handleDragCancel = () => {
    const snapshot = dragSnapshotRef.current;
    endDrag();
    if (snapshot) restoreBoard(snapshot);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    const snapshot = dragSnapshotRef.current || boardTasks;

    const isBelowOver =
      over != null &&
      active.rect.current.translated != null &&
      active.rect.current.translated.top > over.rect.top + over.rect.height / 2;

    // Where the card visually sits (from the optimistic mid-drag state),
    // then the PATCHes realizing it (against the server-truth snapshot).
    // Usually one PATCH (midpoint placement for the moved task); falls
    // back to renumbering the destination column when the gap runs out.
    const placement = over
      ? resolveDropTarget(boardTasks, active.id, over.id, isBelowOver)
      : null;
    const patches = computeDragPatches(snapshot, active.id, placement);
    endDrag();

    if (patches.length === 0) {
      // No server change — but handleDragOver may have left a fractional
      // position behind (e.g. a cross-column hop dragged back home).
      restoreBoard(snapshot);
      return;
    }

    // Apply the optimistic update over the snapshot (integer positions).
    const patchById = new Map(patches.map((p) => [p.id, p]));
    setBoardTasks(
      snapshot.map((task) => {
        const change = patchById.get(task.id);
        if (!change) return task;
        return {
          ...task,
          position: change.nextPosition,
          status: change.nextStatus,
        };
      }),
    );
    setDragError(null);

    patchingRef.current += 1;
    let saving = true;
    const saved = () => {
      if (!saving) return;
      saving = false;
      patchingRef.current -= 1;
      // anything held while saving predates the save: the reconcile
      // below replaces it
      if (patchingRef.current === 0 && !draggingRef.current) {
        pendingSyncRef.current = null;
      }
    };
    try {
      await Promise.all(patches.map((p) => taskApi.update(p.id, p.payload)));
      saved();
      // Background reconcile: keep the optimistic board on screen instead
      // of unmounting the whole board into the loading spinner.
      await refresh({ background: true });
    } catch (err) {
      saved();
      setDragError(err?.message || "Failed to reorder task. Please try again.");
      // A partial failure can leave the server half-updated. If the user
      // has already started ANOTHER drag by the time this rejection lands,
      // don't yank the board out from under it — the refetch below flows
      // through pendingSyncRef and reconciles when that drag ends.
      // Otherwise restore the snapshot for instant feedback, then re-fetch
      // so the board shows the server's actual state rather than silently
      // diverging.
      if (!draggingRef.current) setBoardTasks(snapshot);
      try {
        await refresh({ background: true });
      } catch {
        // useTasks' error state takes over if even the refetch fails
      }
    }
  };

  const handleScopeChange = (nextScope) => {
    const next = new URLSearchParams(searchParams);
    if (nextScope === "all") {
      next.delete("project");
    } else {
      next.set("project", nextScope);
    }
    // Scope is UI state — we don't want the back button to walk through
    // picker changes, and the scope-reset path below also uses replace.
    setSearchParams(next, { replace: true });
  };

  const handleOpenCreate = () => {
    setModalTask(null);
    setModalOpen(true);
  };

  const handleOpenEdit = (task) => {
    setModalTask(task);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalTask(null);
  };

  const handleSave = async (data) => {
    if (modalTask) {
      await updateTask(modalTask.id, data);
    } else {
      await createTask(data);
    }
  };

  const handleDelete = async (id) => {
    await deleteTask(id);
  };

  // Claim / release resolve with the updated task; feed it back into the
  // open modal so the "claimed by" line updates without a reopen.
  const handleClaim = async (id) => {
    try {
      const { data } = await taskApi.claim(id);
      setModalTask(data);
      await refresh({ background: true });
      return data;
    } catch (err) {
      // 409: someone beat us to it — show who, without clobbering edits
      // (the modal only refreshes status/assignee from a new row).
      if (err?.response?.status === 409) {
        try {
          const { data } = await taskApi.get(id);
          setModalTask(data);
        } catch {
          // the banner from the 409 is still shown by the modal
        }
      }
      throw err;
    }
  };

  const handleRelease = async (id) => {
    const { data } = await taskApi.release(id);
    setModalTask(data);
    await refresh({ background: true });
    return data;
  };

  // Archiving takes the task off this board, so the dialog closes.
  const handleArchive = async (id) => {
    await taskApi.archive(id);
    handleCloseModal();
    await Promise.all([refresh({ background: true }), refreshArchivedCount()]);
  };

  const handleUnarchive = async (id) => {
    const { data } = await taskApi.unarchive(id);
    setModalTask(data);
    await Promise.all([refresh({ background: true }), refreshArchivedCount()]);
    return data;
  };

  // "Archive all done" archives exactly what the Done column shows: the
  // whole board, one project, or (for "no project", which the API can't
  // filter) each shown task in turn. The board and count refresh whatever
  // happens, so a partial failure never leaves archived cards on screen.
  const handleArchiveAllDone = async () => {
    try {
      if (scope === "all") {
        return (await taskApi.archiveDone()).data.archived;
      }
      if (Number.isFinite(Number(scope))) {
        return (await taskApi.archiveDone({ project: Number(scope) })).data
          .archived;
      }
      const shown = tasksByStatus[TASK_STATUS.DONE] || [];
      let archived = 0;
      for (const task of shown) {
        try {
          await taskApi.archive(task.id);
          archived += 1;
        } catch (err) {
          // Already archived (a teammate, or the nightly job) is done too.
          if (err?.response?.status === 409) archived += 1;
        }
      }
      if (archived < shown.length) {
        throw new Error(
          `Archived ${archived} of ${shown.length} — ${
            shown.length - archived
          } couldn't be archived. Try again.`,
        );
      }
      return archived;
    } finally {
      await Promise.all([
        refresh({ background: true }),
        refreshArchivedCount(),
      ]);
    }
  };

  // Number("all") and Number("none") are NaN, so a finite check alone
  // handles both the string scopes and a real project id.
  const numericScope = Number(scope);
  const defaultProjectId = Number.isFinite(numericScope) ? numericScope : null;

  const showProjectBadge = scope === "all";

  return (
    <div className="mt-6 w-full px-3 pt-4">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <PageHeader title="Board" subtitle="Kanban view of all tasks" />
        <div className="flex items-center gap-2">
          <LiveStatus status={liveStatus} />
          <ProjectPicker
            scope={scope}
            projects={projects}
            onChange={handleScopeChange}
          />
          <Button onClick={handleOpenCreate}>New task</Button>
        </div>
      </div>

      {dragError && (
        <Alert variant="destructive" className="mb-3 pr-10">
          <AlertDescription>{dragError}</AlertDescription>
          <DismissButton onClick={() => setDragError(null)} />
        </Alert>
      )}

      {deepLinkMiss && (
        <Alert className="mb-3 pr-10">
          <AlertDescription>
            Task {deepLinkMiss} can&apos;t be opened — it&apos;s private to
            someone else, or it was deleted.
          </AlertDescription>
          <DismissButton onClick={() => setDeepLinkMiss(null)} />
        </Alert>
      )}

      {loading && (
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" />
          <span className="sr-only">Loading tasks...</span>
        </div>
      )}

      {/* Full takeover only when there's nothing to show. If a BACKGROUND
          refetch fails after the board is populated, keep the board on
          screen — losing a working board over a blipped reconcile is the
          same unmount-jank this page just got rid of. */}
      {!loading && error && boardTasks.length === 0 && (
        <Alert variant="destructive">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              Failed to load tasks. Please try again.
            </span>
            <Button size="sm" variant="outline" onClick={() => refresh()}>
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && error && boardTasks.length > 0 && (
        <Alert className="mb-3">
          <AlertDescription className="flex items-center gap-3">
            <span className="flex-1">
              Couldn&apos;t refresh tasks — showing the last loaded board.
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => refresh({ background: true })}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {!loading && (!error || boardTasks.length > 0) && (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCorners}
          // Tasks change columns mid-drag (handleDragOver), so droppable
          // rects go stale unless re-measured continuously.
          measuring={{ droppable: { strategy: MeasuringStrategy.Always } }}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragCancel={handleDragCancel}
          onDragEnd={handleDragEnd}
        >
          <div className="flex gap-3 overflow-auto">
            {COLUMNS.map((column) => (
              <TaskColumn
                key={column.status}
                status={column.status}
                label={column.label}
                tasks={tasksByStatus[column.status] || []}
                showProjectBadge={showProjectBadge}
                projectsById={projectsById}
                onCardClick={handleOpenEdit}
                droppableId={`${COLUMN_DROP_PREFIX}${column.status}`}
                footer={
                  column.status === TASK_STATUS.DONE ? (
                    <DoneColumnFooter
                      // A fresh footer per scope: a note or error about
                      // one project never lingers onto another.
                      key={scope}
                      doneCount={(tasksByStatus[TASK_STATUS.DONE] || []).length}
                      archivedCount={archivedCount}
                      archiveHref={
                        Number.isFinite(Number(scope))
                          ? `/tasks/archive?project=${Number(scope)}`
                          : "/tasks/archive"
                      }
                      onArchiveAll={handleArchiveAllDone}
                    />
                  ) : null
                }
              />
            ))}
          </div>
          {/* The floating card that follows the pointer. Rendered in a
              portal, so it is never clipped by the columns' overflow and
              never re-parents mid-drag — the in-list card stays behind as
              a dimmed placeholder. */}
          <DragOverlay dropAnimation={{ duration: 180, easing: "ease" }}>
            {activeDragTask ? (
              <TaskCardView
                task={activeDragTask}
                project={
                  showProjectBadge && activeDragTask.project != null
                    ? projectsById[activeDragTask.project]
                    : null
                }
                className="shadow-lg"
                style={{ cursor: "grabbing" }}
              />
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <TaskModal
        show={modalOpen}
        onHide={handleCloseModal}
        projects={projects}
        task={modalTask}
        defaultProjectId={defaultProjectId}
        onSave={handleSave}
        onDelete={handleDelete}
        users={users}
        usersError={usersError}
        onRetryUsers={refreshUsers}
        me={me}
        onClaim={handleClaim}
        onRelease={handleRelease}
        onArchive={handleArchive}
        onUnarchive={handleUnarchive}
        onAttachmentsChange={() => refresh({ background: true })}
      />
    </div>
  );
}

/** The X on a dismissible alert. */
function DismissButton({ onClick }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="absolute top-1.5 right-1.5 size-7"
      aria-label="Close"
      onClick={onClick}
    >
      <X />
    </Button>
  );
}

DismissButton.propTypes = { onClick: PropTypes.func.isRequired };

export default TaskBoard;
